import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

import { createAccount } from '@/features/auth/api/account';
import type { Account, ScopeContext } from '@/features/auth/model/types';
import { useLanguage } from '@/shared/hooks/use-language';
import { useToast } from '@/shared/hooks/use-toast';
import { ScopeContextStorage } from '@/shared/lib/scope-storage';
import { DebugLogger } from '@/shared/lib/debug';
import { exitToLogin } from '@/shared/lib/session-exit';
import { parseBackendErrors } from '@/shared/lib/backend-errors';
import { isUnreadableResponseError } from '@/shared/lib/response-shape';
import {
  UNIQUE_ID_MAX_LENGTH,
  deriveUniqueId,
  uniqueIdErrorKey,
  uniqueIdErrorMessage,
} from '@/shared/lib/unique-id';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { FormField } from '@/shared/ui/form-field';
import { Logo } from '@/shared/ui/logo';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/alert-dialog';

const CREATE_ACCOUNT_FIELD_MAP: Record<string, string> = {
  'accounts.name': 'name',
  'accounts.unique_id': 'uniqueId',
};

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 36;
const HTML_TAG_PATTERN = /<[a-zA-Z/!]/;

/**
 * Error codes that prove the request was rejected *before* the account was written — the
 * validator, the permission check, or the gateway turned it away. Only these may re-arm the
 * form: this endpoint is not idempotent and an Account has no close route, so a retry after
 * anything else risks a duplicate nobody can remove.
 *
 * The presence of an `errors` array is not the test. A backend 500 comes back through the
 * gateway as a well-formed `mayday-error` body, and by then the write may already have run.
 * Anything not listed here is treated as unknown, which fails closed.
 */
const REJECTED_BEFORE_WRITE_CODES = new Set([
  'invalid-structure',
  'invalid-type',
  'invalid-value',
  'invalid-permission',
  'invalid-credentials',
  'already-exists',
  'not-found',
  'route-not-found',
  'blocked-scope',
  'invalid-version',
  'deprecated-version',
  'rate-limit',
]);

function rejectedBeforeWrite(err: unknown): boolean {
  const errors = (err as { errors?: { code?: string }[] })?.errors;
  if (errors?.length) {
    return errors.every(error => error.code && REJECTED_BEFORE_WRITE_CODES.has(error.code));
  }
  const code = (err as { code?: string })?.code;
  return Boolean(code && REJECTED_BEFORE_WRITE_CODES.has(code));
}

type Outcome = 'editing' | 'denied' | 'ambiguous';

function AccountCreationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { toast } = useToast();

  const sessionId = location.state?.sessionId || ScopeContextStorage.getCurrentSessionId();
  const scopeContext: ScopeContext | null =
    location.state?.scopeContext || (sessionId ? ScopeContextStorage.getScopeContext(sessionId) : null);
  const holderId =
    location.state?.holderId ?? scopeContext?.accountHolder?.id ?? scopeContext?.account?.holder_id;
  const origin = location.state?.origin;

  const [name, setName] = useState('');
  const [uniqueId, setUniqueId] = useState('');
  const [uniqueIdEdited, setUniqueIdEdited] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [outcome, setOutcome] = useState<Outcome>('editing');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (sessionId) {
      return;
    }
    DebugLogger.error('Account creation opened without a session', { state: location.state });
    toast({
      title: t('accountCreate.createError'),
      description: t('accountHolder.noSessionId'),
      variant: 'destructive',
    });
    exitToLogin();
  }, [sessionId]);

  const clearFieldError = (field: string) => {
    setFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }
      const next = { ...previous };
      delete next[field];
      return next;
    });
  };

  const handleNameChange = (value: string) => {
    setName(value);
    clearFieldError('name');
    if (!uniqueIdEdited) {
      const { derived, errorKey } = deriveUniqueId(value);
      setUniqueId(derived);
      const uniqueIdError = uniqueIdErrorMessage(t, errorKey, 'accountCreate.uniqueIdRequired');
      if (uniqueIdError) {
        setFieldErrors((previous) => ({ ...previous, uniqueId: uniqueIdError }));
      } else {
        clearFieldError('uniqueId');
      }
    }
  };

  const handleUniqueIdChange = (value: string) => {
    setUniqueId(value);
    clearFieldError('uniqueId');
    setUniqueIdEdited(value.length > 0);
  };

  const holderScopeContext = () => ({
    ...scopeContext,
    account: undefined,
    scopeId: holderId,
    scopeType: 'holder' as const,
  });

  const goBack = () => {
    if (origin === 'console') {
      navigate(-1);
      return;
    }
    navigate('/account-selection', { state: { sessionId, scopeContext } });
  };

  /**
   * Reaches the account list from every entry point, which Back does not: from the switcher,
   * Back returns to the console. After an outcome the client could not verify, the list is the
   * only place the user can find out what actually happened, so the control that says so has to
   * go there. Clears `account` for the same reason the success path does — the picker
   * auto-forwards past the list whenever the context it receives already holds one.
   */
  const goToAccounts = () => {
    navigate('/account-selection', {
      state: { sessionId, scopeContext: holderScopeContext() },
    });
  };

  const localErrorsFor = (trimmedName: string, trimmedUniqueId: string) => {
    const localErrors: Record<string, string> = {};

    if (!trimmedName) {
      localErrors.name = t('accountCreate.nameRequired');
    } else if (trimmedName.length < NAME_MIN_LENGTH || trimmedName.length > NAME_MAX_LENGTH) {
      localErrors.name = t('accountCreate.nameLength');
    } else if (HTML_TAG_PATTERN.test(trimmedName)) {
      localErrors.name = t('accountCreate.nameHtml');
    }

    const uniqueIdError = uniqueIdErrorMessage(t, uniqueIdErrorKey(trimmedUniqueId), 'accountCreate.uniqueIdRequired');
    if (uniqueIdError) {
      localErrors.uniqueId = uniqueIdError;
    }

    return localErrors;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (isCreating || outcome !== 'editing') {
      return;
    }

    const localErrors = localErrorsFor(name.trim(), uniqueId.trim());
    if (Object.keys(localErrors).length > 0) {
      setFieldErrors(localErrors);
      setError(null);
      return;
    }

    setFieldErrors({});
    setError(null);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (isCreating || outcome !== 'editing') {
      return;
    }

    setConfirmOpen(false);
    setIsCreating(true);

    let created: Account;
    try {
      created = await createAccount(sessionId, holderId, {
        name: name.trim(),
        unique_id: uniqueId.trim(),
      });
    } catch (err) {
      handleFailure(err);
      return;
    }

    toast({
      title: t('accountCreate.createdTitle'),
      description: t('accountCreate.createdBody').replace('{name}', created.name),
    });
    navigate('/account-selection', {
      state: {
        sessionId,
        scopeContext: holderScopeContext(),
        createdAccount: created,
      },
      replace: true,
    });
  };

  const handleFailure = (err: unknown) => {
    DebugLogger.error('Account creation failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });

    if (isUnreadableResponseError(err)) {
      setIsCreating(false);
      setOutcome('ambiguous');
      setError(t('accountCreate.createdUnreadable'));
      return;
    }

    if (!rejectedBeforeWrite(err)) {
      setIsCreating(false);
      setOutcome('ambiguous');
      setError(t('accountCreate.ambiguousFailure'));
      return;
    }

    if ((err as { code?: string })?.code === 'invalid-permission') {
      setIsCreating(false);
      setOutcome('denied');
      setError(t('accountCreate.permissionDenied'));
      return;
    }

    const { fieldErrors: backendFieldErrors, generalError } = parseBackendErrors(
      err,
      CREATE_ACCOUNT_FIELD_MAP,
    );

    if (Object.keys(backendFieldErrors).length > 0) {
      setFieldErrors(backendFieldErrors);
      setError(generalError);
      setIsCreating(false);
      return;
    }

    setError(generalError || (err instanceof Error ? err.message : t('accountCreate.createError')));
    setIsCreating(false);
  };

  if (!sessionId) {
    return null;
  }

  const backButton = (
    <Button variant="ghost" onClick={goBack} className="gap-2" data-testid="account-create-back">
      <ArrowLeft className="w-4 h-4" />
      {t('accountCreate.back')}
    </Button>
  );

  if (!holderId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-8">
        <div className="w-full max-w-2xl text-center">
          <Logo className="justify-center mb-4" />
          <p className="text-muted-foreground" data-testid="account-create-no-scope">
            {t('accountCreate.noHolder')}
          </p>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
            {backButton}
            <Button variant="ghost" onClick={exitToLogin}>
              {t('account.backToLogin')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background animate-fadeIn p-8">
      <div className="w-full max-w-2xl">
        <div className="mb-8 text-center">
          <Logo className="justify-center mb-4" />
          <h1 className="text-2xl font-semibold text-foreground mb-2">{t('accountCreate.title')}</h1>
          <p className="text-muted-foreground">{t('accountCreate.subtitle')}</p>
        </div>

        <div className="mb-4">{backButton}</div>

        <Card className="bg-card border-border p-8">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="account-create-form">
            {error && (
              <div
                className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-md text-sm"
                data-testid={
                  outcome === 'denied'
                    ? 'account-create-no-permission'
                    : outcome === 'ambiguous'
                      ? 'account-create-unreadable'
                      : 'account-create-error'
                }
              >
                {error}
              </div>
            )}

            <FormField
              id="accountName"
              type="text"
              label={t('accountCreate.nameLabel')}
              placeholder={t('accountCreate.namePlaceholder')}
              value={name}
              maxLength={NAME_MAX_LENGTH}
              onChange={handleNameChange}
              error={fieldErrors.name}
              disabled={outcome !== 'editing'}
              data-testid="account-create-name"
              required
            />

            <FormField
              id="accountUniqueId"
              type="text"
              label={t('accountCreate.uniqueIdLabel')}
              placeholder={t('uniqueId.placeholder')}
              hint={t('uniqueId.hint')}
              value={uniqueId}
              maxLength={UNIQUE_ID_MAX_LENGTH}
              onChange={handleUniqueIdChange}
              error={fieldErrors.uniqueId}
              disabled={outcome !== 'editing'}
              data-testid="account-create-unique-id"
              required
            />

            {outcome === 'editing' ? (
              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={isCreating}
                data-testid="account-create-submit"
              >
                {isCreating ? t('accountCreate.creating') : t('accountCreate.submit')}
              </Button>
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={goToAccounts}
                data-testid="account-create-go-to-accounts"
              >
                {t('accountCreate.goToAccounts')}
              </Button>
            )}
          </form>
        </Card>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('accountCreate.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {(scopeContext?.accountHolder?.name
                ? t('accountCreate.confirmBodyUnderHolder')
                    .replace('{holder}', scopeContext.accountHolder.name)
                : t('accountCreate.confirmBody'))
                .replace('{name}', name.trim())
                .replace('{uniqueId}', uniqueId.trim())}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('accountCreate.confirmCancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isCreating}
              data-testid="account-create-confirm"
            >
              {t('accountCreate.confirmAction')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AccountCreationPage;
